import type {ReactElement} from 'react';
import {useId} from 'react';
import {Text, TextSize} from '@Components/text';
import {VisuallyHiddenText} from '@Components/visually-hidden-text/visually-hidden-text';
import type {LabeledValueRowProps} from './labeled-value-row.types';
import styles from './labeled-value-row.module.scss';

/**
 * A list item showing a label over its value, with an optional link that acts on it. Render it inside a `<ul>`. The link
 * opens in a new tab, so the page the row sits on stays open, and its accessible name says so.
 */
export function LabeledValueRow({label, children, action}: LabeledValueRowProps): ReactElement {
  const labelId = useId();
  const actionId = useId();
  const newTabHintId = useId();

  const renderAction = (): ReactElement | null => {
    if (action === undefined) {
      return null;
    }

    return (
      <>
        <Text
          val={action.label}
          href={action.href}
          size={TextSize.SM}
          bold
          className={styles.labeledValueRowAction}
          attributes={{id: actionId, 'aria-labelledby': `${actionId} ${labelId} ${newTabHintId}`, target: '_blank', rel: 'noopener noreferrer'}}
        />
        <VisuallyHiddenText id={newTabHintId} val={window.i18next.t('pmwjs_ai_opens_in_new_tab')} />
      </>
    );
  };

  return (
    <li className={styles.labeledValueRow}>
      <div className={styles.labeledValueRowBody}>
        <Text val={label} size={TextSize.SM} bold className={styles.labeledValueRowLabel} attributes={{id: labelId}} />
        {children}
      </div>
      {renderAction()}
    </li>
  );
}
